import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EXAMPLE_BUSINESSES, VIDEO_STYLES, type BriefDraft, type ExampleBusiness, type ProjectView } from "@joogoos/contracts";
import { api } from "../../api.js";

/**
 * The landing stage (docs/03): a real example of every style a drawn video comes in, each made by the renderer for a
 * made-up business (`pnpm --filter @joogoos/render gallery`, served from `public/gallery/`). A tile plays when the
 * pointer is on it. Picking one is the backwards direction — it makes the project a drawn video in that style, and the
 * chat asks what it is about.
 *
 * One fixed example per style, the same for every business, so the screen claims no ranking, trend or filter (docs/14).
 * The copy does not count them: it said "these six" until a seventh style arrived (2026-09-28).
 *
 * Since 2026-10-13 a person can also see all ten drawn for one made-up kind of business (`EXAMPLE_BUSINESSES`, drawn
 * by `pnpm --filter @joogoos/render examples` into `public/examples/<type>/`): the same sheet of words in every style.
 * It is a switch the person presses, the same for everyone, and it ranks nothing; with none pressed the gallery is what
 * it was, each style for its own business.
 */
type Style = (typeof VIDEO_STYLES)[number];

/** "plumber", "IT support firm": the type as a business is said in a sentence. */
const kindOf = (b: ExampleBusiness) => {
  const t = b.type.toLowerCase().replace(/^it /, "IT ");
  return /support|cleaning/.test(t) ? `${t} firm` : t;
};
/** "a plumber", "an accountant", "an IT support firm". */
const aBusiness = (b: ExampleBusiness) => `${/^[aeiou]/i.test(kindOf(b)) ? "an" : "a"} ${kindOf(b)}`;

const patchOf = (s: Style): BriefDraft => ({ kind: "video", format: "story", look: "drawn", style: s.id });
const note = (s: Style) => `Start from “${s.name}” — ${s.look}`;

function Tile({ s, b, onPick, disabled }: { s: Style; b: ExampleBusiness | null; onPick: () => void; disabled: boolean }) {
  const video = useRef<HTMLVideoElement>(null);
  const play = () => { const v = video.current; if (v) { v.currentTime = 0; void v.play()?.catch(() => undefined); } };
  const stop = () => video.current?.pause();
  return (
    <button onClick={onPick} disabled={disabled} onMouseEnter={play} onMouseLeave={stop} onFocus={play} onBlur={stop}
      className="text-left flex flex-col gap-1.5 group disabled:opacity-60">
      <div className="relative rounded-[10px] overflow-hidden bg-stage aspect-[9/16]">
        <video key={b?.id ?? "gallery"} ref={video} src={b ? `/examples/${b.id}/${s.id}.mp4` : `/gallery/${s.id}.mp4`} poster={b ? `/examples/${b.id}/${s.id}.webp` : `/gallery/${s.id}.webp`} muted loop playsInline preload="none"
          aria-hidden className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-x-0 bottom-0 px-2.5 pt-8 pb-2 bg-gradient-to-t from-black/75 to-transparent text-white">
          <div className="text-[13px] font-bold">{s.name}</div>
          <div className="text-[11px] opacity-85">{s.look}</div>
        </div>
      </div>
      <span className="text-[11px] text-muted">for {b ? aBusiness(b) : s.sample} · suits {s.suits}</span>
    </button>
  );
}

export function Gallery({ project, onStart, starting }: {
  project: ProjectView | null;
  onStart: (patch: BriefDraft, text: string) => void;
  starting: boolean;
}) {
  const qc = useQueryClient();
  const pick = useMutation({
    mutationFn: (s: Style) => api.answer(project!.id, patchOf(s), note(s)),
    onSuccess: (p) => { qc.setQueryData(["project", p.id], p); void qc.invalidateQueries({ queryKey: ["projects"] }); },
  });
  const choose = (s: Style) => (project ? pick.mutate(s) : onStart(patchOf(s), note(s)));
  const busy = pick.isPending || starting;
  const [type, setType] = useState<ExampleBusiness | null>(null);
  const chip = (on: boolean) =>
    `rounded-full px-3 py-1 text-[12px] border ${on ? "bg-ink text-white border-ink font-semibold" : "bg-panel text-ink-2 border-line-2 hover:border-ink"}`;

  return (
    <div className="p-4 flex flex-col gap-3.5">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-[15px] font-semibold">Start from a style</h2>
        <span className="text-[12.5px] text-muted">
          {type ? `real videos Joogoos drew, all for a made-up ${kindOf(type)}` : "real videos Joogoos drew, each for a made-up business"}
        </span>
      </div>

      <div role="group" aria-label="Show them for" className="flex flex-wrap items-center gap-1.5">
        <span className="text-[12px] text-muted mr-1">Show them for</span>
        <button type="button" aria-pressed={type === null} onClick={() => setType(null)} className={chip(type === null)}>A mix</button>
        {EXAMPLE_BUSINESSES.map((b) => (
          <button key={b.id} type="button" aria-pressed={type?.id === b.id} onClick={() => setType(b)} className={chip(type?.id === b.id)}>{b.type}</button>
        ))}
      </div>

      <div role="group" aria-label="Styles" className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3.5">
        {VIDEO_STYLES.map((s) => <Tile key={s.id} s={s} b={type} onPick={() => choose(s)} disabled={busy} />)}
      </div>

      <p className="text-xs text-muted mt-2 leading-relaxed max-w-2xl">
        These are the same for every business. Pick a kind of business to see all ten drawn with the same words.
        Picking a style costs nothing: it makes a drawn video in that style,
        in your colours and your words, and the chat asks what it is about. A post, or a video from your photos, starts
        in the chat.
      </p>
    </div>
  );
}
